import { Button, Flex, Select, Text } from "@radix-ui/themes";
import {
  hitlApi,
  useCreateAutomatedCheckoutMutation,
  useLazyLatestActiveCheckoutQuery,
} from "../../api/hitl-api";
import { useNavigate, useParams } from "react-router-dom";
import { GlobeIcon } from "@radix-ui/react-icons";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../../store";
import { useEffectOnce } from "react-use";
import { getCheckoutUrl, getGuideUrl } from "../../shared/consts";
import toast from "react-hot-toast";
import { getFieldErrorMessage } from "../../api/helpers/error";

const langs = [
  { label: "English", value: "en" },
  { label: "Turkish", value: "tr" },
];

export function Start() {
  const [createAutomatedCheckout, result] = useCreateAutomatedCheckoutMutation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { zoneId = "", storeKey = "" } = useParams();

  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [trigger, latesActiveCheckoutResult] = useLazyLatestActiveCheckoutQuery();

  useEffectOnce(() => {
    trigger(zoneId)
      .unwrap()
      .then((data) => {
        if (data && data.result) {
          dispatch(hitlApi.util.upsertQueryData("checkout", data.result.id, data)).then(() => {
            navigate(
              getCheckoutUrl({
                zoneId,
                storeKey,
                checkoutKey: data.result.id,
                mode: "sku-in-box",
              })
            );
          });
        }
      });
  });

  return (
    <div className="bg-tezzro-bg p-8 h-screen">
      <div className="relative bg-white border rounded-2xl border-card-border w-full h-full flex items-center justify-center">
        <div className="absolute top-4 right-5">
          <Select.Root value={lang} onValueChange={(value) => i18n.changeLanguage(value)} size="3">
            <Select.Trigger variant="ghost" className="">
              <Flex align="center" gap="2">
                <GlobeIcon />
                {langs.find((item) => item.value === lang)?.label}
              </Flex>
            </Select.Trigger>
            <Select.Content position="popper">
              {langs.map((lang) => (
                <Select.Item value={lang.value} key={lang.value}>
                  {lang.label}
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Root>
        </div>
        <div className="h-full">
          <div className="min-w-[400px] h-full">
            <div className="flex flex-col justify-center items-center h-full">
              <figure className="mb-6">
                <img src="/intro.png" className="object-contain max-w-[530px]" />
              </figure>
              <Text size="8" weight="bold" mb="7">
                {t("welcome")}{" "}
                <span className="inline-block animate-wave origin-[70%_70%] align-bottom">
                  <img className="w-[40px] h-[40px]" src="/waving-hand.png" />
                </span>
              </Text>
              <div className="flex gap-4 relative">
                <div className="absolute -top-[20px] -left-[160px]">
                  <Text
                    as="p"
                    weight="bold"
                    className="w-[136px] font-['Grandstander'] absolute -top-[80px] -left-12 rotate-[-8deg]"
                  >
                    {t("guideHint1")}
                    <br />
                    {t("guideHint2")}
                  </Text>
                  <img src="/arrow.svg" />
                </div>
                <Button
                  size="4"
                  radius="medium"
                  loading={result.isLoading || latesActiveCheckoutResult.isLoading}
                  onClick={async () => {
                    navigate(
                      getGuideUrl({
                        zoneId,
                        storeKey,
                        mode: "sku-in-box",
                      })
                    );
                  }}
                >
                  {t("startGuide")}
                </Button>
                <Button
                  size="4"
                  radius="medium"
                  variant="soft"
                  loading={result.isLoading || latesActiveCheckoutResult.isLoading}
                  onClick={async () => {
                    const res = await trigger(zoneId).unwrap();

                    if (res.result && res.result.id) {
                      dispatch(hitlApi.util.upsertQueryData("checkout", res.result.id, res)).then(() => {
                        navigate(
                          getCheckoutUrl({
                            zoneId,
                            storeKey,
                            checkoutKey: res.result.id,
                            mode: "sku-in-box",
                          })
                        );
                      });
                    } else {
                      createAutomatedCheckout({ zoneId })
                        .unwrap()
                        .then((res) => {
                          navigate(
                            getCheckoutUrl({
                              zoneId,
                              checkoutKey: res.result.key,
                              mode: "sku-in-box",
                              storeKey,
                            })
                          );
                        })
                        .catch((err) => {
                          if (err?.response?.data?.type === "logic") {
                            toast.error(getFieldErrorMessage(err));
                          } else {
                            toast.error("Unknown error. Please try again later.");
                          }
                        });
                    }
                  }}
                >
                  {t("start")}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
